// الميزانية العمومية — بالتصميم الموحّد
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { Box, Paper, Typography, TextField, CircularProgress, Alert } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventIcon from '@mui/icons-material/Event';

import accountingReportsApi from '../../api/accountingReportsApi';
import { ReportPage, ReportFilters, fieldSx, fmtMoney } from '../../components/Reports/ReportKit';
import { PrintSheet, PrintSummary, useReportPrint, useCompanyInfo, fmtDate } from '../../components/Reports/PrintKit';
import { FinancialSection, PrintFinancialSection, STATEMENT_ROUTE } from './FinancialSection';

const today = () => dayjs().format('YYYY-MM-DD');

export default function BalanceSheetReport() {
  const navigate = useNavigate();
  const company = useCompanyInfo();
  const [asOf, setAsOf] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    if (!asOf) return;
    setLoading(true);
    try {
      const res = await accountingReportsApi.getBalanceSheet({ asOfDate: asOf });
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error('فشل جلب الميزانية العمومية');
    } finally {
      setLoading(false);
    }
  }, [asOf]);

  useEffect(() => {
    const id = setTimeout(fetchReport, 300);
    return () => clearTimeout(id);
  }, [fetchReport]);

  const assets = Number(data?.totalAssets) || 0;
  const liabilities = Number(data?.totalLiabilities) || 0;
  const equity = Number(data?.totalEquity) || 0;
  const liabEq = Number(data?.totalLiabilitiesAndEquity ?? liabilities + equity) || 0;
  const diff = Math.round((assets - liabEq) * 100) / 100;
  const balanced = Math.abs(diff) < 0.01;

  const openStatement = (r) =>
    navigate(`${STATEMENT_ROUTE}?accountId=${r.accountId}&from=${dayjs(asOf).startOf('year').format('YYYY-MM-DD')}&to=${asOf}`);

  const printRef = useRef(null);
  const handlePrint = useReportPrint(printRef, 'الميزانية-العمومية');

  return (
    <ReportPage
      title="الميزانية العمومية"
      icon={<AccountBalanceIcon />}
      badges={[{ label: `كما في ${fmtDate(asOf)}`, icon: <EventIcon /> }]}
      actions={[
        { label: 'تحديث', icon: <RefreshIcon />, onClick: fetchReport, disabled: loading },
        { label: 'طباعة', icon: <PrintOutlinedIcon />, onClick: handlePrint, disabled: !data, primary: true },
      ]}
      stats={data ? [
        { label: 'إجمالي الأصول', value: fmtMoney(assets), icon: <Inventory2OutlinedIcon />, color: 'primary' },
        { label: 'إجمالي الخصوم', value: fmtMoney(liabilities), icon: <CreditCardOutlinedIcon />, color: 'error' },
        { label: 'حقوق الملكية', value: fmtMoney(equity), icon: <PeopleAltOutlinedIcon />, color: 'warning' },
        {
          label: balanced ? 'الميزانية متوازنة' : 'فرق غير متوازن', value: balanced ? '✓ 0.00' : fmtMoney(Math.abs(diff)),
          icon: balanced ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />, color: balanced ? 'success' : 'error', highlight: true,
        },
      ] : []}
      filters={
        <ReportFilters onApply={fetchReport} onReset={() => setAsOf(today())} loading={loading}>
          <TextField
            size="small" fullWidth type="date" label="كما في تاريخ" value={asOf}
            onChange={e => e.target.value && setAsOf(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ ...fieldSx, '& input': { direction: 'ltr', textAlign: 'right' } }}
          />
        </ReportFilters>
      }
    >
      {data && (
        <Box sx={{ display: 'none' }}>
          <PrintSheet ref={printRef} title="الميزانية العمومية" period={`كما في ${fmtDate(asOf)}`} company={company}>
            <PrintSummary items={[
              ['الأصول', fmtMoney(assets)],
              ['الخصوم', fmtMoney(liabilities)],
              ['حقوق الملكية', fmtMoney(equity)],
              ['الخصوم + حقوق الملكية', fmtMoney(liabEq)],
            ]} />
            <PrintFinancialSection title="الأصول" rows={data.assets} total={assets} />
            <PrintFinancialSection title="الخصوم" rows={data.liabilities} total={liabilities} />
            <PrintFinancialSection title="حقوق الملكية" rows={data.equity} total={equity} />
            <div style={{ border: '2px solid #111827', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 800 }}>
              <span>إجمالي الخصوم وحقوق الملكية</span>
              <span style={{ direction: 'ltr' }}>{fmtMoney(liabEq)}</span>
            </div>
          </PrintSheet>
        </Box>
      )}

      {loading && !data ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 260 }}><CircularProgress /></Box>
      ) : data && (
        <Box sx={{ display: 'grid', gap: 1.5, opacity: loading ? .6 : 1, transition: 'opacity .2s' }}>
          {!balanced && (
            <Alert severity="warning" sx={{ borderRadius: '12px' }}>
              الأصول لا تساوي الخصوم + حقوق الملكية بفرق <b dir="ltr">{fmtMoney(diff)}</b>.
              من الأسباب الشائعة: صافي ربح السنة الحالية لم يُضَف لحقوق الملكية (لا يوجد قيد إقفال)، أو قيود غير متوازنة.
            </Alert>
          )}

          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, alignItems: 'start' }}>
            <FinancialSection title="الأصول" rows={data.assets || []} total={assets} color="#4f46e5"
              icon={<Inventory2OutlinedIcon />} onRowClick={openStatement} />

            <Box sx={{ display: 'grid', gap: 1.5 }}>
              <FinancialSection title="الخصوم" rows={data.liabilities || []} total={liabilities} color="#e11d48"
                icon={<CreditCardOutlinedIcon />} onRowClick={openStatement} />
              <FinancialSection title="حقوق الملكية" rows={data.equity || []} total={equity} color="#d97706"
                icon={<PeopleAltOutlinedIcon />} onRowClick={openStatement} />
              <Paper elevation={0} sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1,
                px: 2, py: 1.5, borderRadius: '14px', color: '#fff',
                background: 'linear-gradient(120deg,#1e293b,#334155)',
              }}>
                <Typography sx={{ fontWeight: 800 }}>إجمالي الخصوم وحقوق الملكية</Typography>
                <Typography sx={{ fontWeight: 900, fontSize: 20, direction: 'ltr' }}>{fmtMoney(liabEq)}</Typography>
              </Paper>
            </Box>
          </Box>
        </Box>
      )}
    </ReportPage>
  );
}
